import React, { useCallback } from 'react';
import { OnboardingProvider, useOnboarding } from '@/context/OnboardingContext.tsx';
import { OnboardingCheckoutProvider, useSetupEntryGate } from '@/context/OnboardingCheckoutContext.tsx';
import { OnboardingForm } from '@/components/on-boarding/OnboardingForm.tsx';
import { useHouseContext } from '@/context/HouseContext.tsx';
import { GlobalOverlayLoader } from '@/components/common/GlobalOverlayLoader.tsx';
import { useRouterState } from '@tanstack/react-router';
import { useAppNavigation } from '@/context/AppNavigationContext.tsx';
import { Routes } from '@/routes.ts';

/**
 * Onboarding page for adding a new property.
 *
 * The normal flow pays before navigating here, but direct navigation bypasses that gate — so the
 * credit validation runs here too, redirecting a creditless user to checkout on entry rather than
 * letting them discover it on a nonfunctional step-1 Next button.
 */
const NewPropertyOnboardingPage: React.FC = () => {
	const { navigateToMyBundles } = useAppNavigation();
	const { completeHouses, setSelectedHouseId } = useHouseContext();
	const { currentStep } = useOnboarding();
	const { isLoading: isHouseLoading } = useHouseContext();

	useSetupEntryGate();

	const handleLogoClick = useCallback(async () => {
		if (completeHouses.length) {
			setSelectedHouseId(completeHouses[0].id);
		}
		await navigateToMyBundles();
	}, [completeHouses, setSelectedHouseId, navigateToMyBundles]);

	const routerState = useRouterState();
	const isLeaving =
		routerState.resolvedLocation?.pathname === Routes.HOUSE_SETUP_NEW &&
		routerState.location.pathname !== Routes.HOUSE_SETUP_NEW;

	if (isLeaving) {
		return <GlobalOverlayLoader text="Loading..." />;
	}

	if (isHouseLoading) {
		return <GlobalOverlayLoader text="Loading..." />;
	}

	return <OnboardingForm key={currentStep} handleLogoClick={handleLogoClick} />;
};

const SetupNew: React.FC = () => {
	return (
		<OnboardingProvider>
			<OnboardingCheckoutProvider>
				<NewPropertyOnboardingPage />
			</OnboardingCheckoutProvider>
		</OnboardingProvider>
	);
};

export const Route = createFileRoute({
	component: SetupNew,
});
